import { Fragment, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ChevronRight, Download } from 'lucide-react';
import { api } from '@/lib/api';
import type { Program } from '@/lib/types';
import { cn, fmtDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/field';
import { Badge, Card, Spinner } from '@/components/ui/misc';

type Json = Record<string, unknown> | null;
interface Row {
  id: string;
  at: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actor: { id: string; name: string; email: string } | null;
  actorLabel: string | null;
  ip: string | null;
  before: Json;
  after: Json;
}
interface Facets {
  actions: string[];
  entityTypes: string[];
  actors: Array<{ id: string; name: string }>;
}

const NOISE = new Set(['updatedAt', 'createdAt']);
const ACTION_TONE: Record<string, 'ok' | 'bad' | 'warn' | 'info' | 'neutral' | 'brand'> = {
  create: 'ok',
  update: 'info',
  soft_delete: 'bad',
  delete: 'bad',
  restore: 'ok',
  login: 'neutral',
  logout: 'neutral',
  login_failed: 'warn',
  mfa_failed: 'warn',
  role_change: 'brand',
  mfa_reset: 'brand',
};

/** Fields whose value differs between before and after. */
function changed(before: Json, after: Json) {
  const keys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
  return [...keys].filter((k) => !NOISE.has(k) && JSON.stringify(before?.[k]) !== JSON.stringify(after?.[k]));
}

const show = (v: unknown) => (v === null || v === undefined ? '' : typeof v === 'string' ? v : JSON.stringify(v));

function summary(r: Row) {
  if (r.action === 'create') return show(r.after?.name ?? r.after?.title ?? r.after?.key ?? r.after?.email ?? '');
  if (r.before && r.after) return changed(r.before, r.after).join(', ');
  if (r.after && !r.before)
    return Object.entries(r.after)
      .map(([k, v]) => `${k}: ${show(v)}`)
      .join(' · ');
  return '';
}

export function AuditLog({ program }: { program: Program }) {
  const base = `/api/programs/${program.id}/admin`;
  const [f, setF] = useState({ actorId: '', action: '', entityType: '', from: '', to: '' });
  const [open, setOpen] = useState<string | null>(null);
  const facets = useQuery({
    queryKey: ['admin', program.id, 'audit-facets'],
    queryFn: () => api.get<Facets>(`${base}/audit/facets`),
  });

  const params = new URLSearchParams(Object.entries(f).filter(([, v]) => v));
  const rows = useInfiniteQuery({
    queryKey: ['admin', program.id, 'audit', params.toString()],
    queryFn: ({ pageParam }) =>
      api.get<{ rows: Row[]; nextBefore: string | null }>(
        `${base}/audit?${params.toString()}${pageParam ? `${params.size ? '&' : ''}before=${pageParam}` : ''}`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });
  const all = rows.data?.pages.flatMap((p) => p.rows) ?? [];
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  return (
    <Card>
      <div className="flex flex-wrap items-end gap-2 border-b border-border px-5 py-3">
        <Select aria-label="Actor" className="h-8 w-44 text-[13px]" value={f.actorId} onChange={set('actorId')}>
          <option value="">Anyone</option>
          {facets.data?.actors.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        <Select aria-label="Action" className="h-8 w-40 text-[13px]" value={f.action} onChange={set('action')}>
          <option value="">Any action</option>
          {facets.data?.actions.map((a) => (
            <option key={a}>{a}</option>
          ))}
        </Select>
        <Select
          aria-label="Record type"
          className="h-8 w-44 text-[13px]"
          value={f.entityType}
          onChange={set('entityType')}
        >
          <option value="">Any record</option>
          {facets.data?.entityTypes.map((a) => (
            <option key={a}>{a}</option>
          ))}
        </Select>
        <Input aria-label="From" type="date" className="h-8 w-38 text-[13px]" value={f.from} onChange={set('from')} />
        <Input aria-label="To" type="date" className="h-8 w-38 text-[13px]" value={f.to} onChange={set('to')} />
        <a
          href={`${base}/audit.csv?${params.toString()}`}
          className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-border-strong bg-surface px-2.5 text-[13px] font-medium shadow-sm hover:bg-surface-2"
        >
          <Download className="size-4" /> Download CSV
        </a>
      </div>
      {rows.isLoading ? (
        <Spinner />
      ) : all.length === 0 ? (
        <p className="px-5 py-8 text-center text-sm text-muted">No entries match.</p>
      ) : (
        <table className="w-full table-fixed text-[13px]">
          <thead className="border-b border-border text-left text-[11px] font-medium uppercase tracking-wide text-subtle">
            <tr>
              <th className="w-8" />
              <th className="w-44 px-2 py-2">When</th>
              <th className="w-44 px-2 py-2">Who</th>
              <th className="w-28 px-2 py-2">Action</th>
              <th className="w-36 px-2 py-2">Record</th>
              <th className="px-2 py-2">Detail</th>
            </tr>
          </thead>
          <tbody>
            {all.map((r) => {
              const isOpen = open === r.id;
              const keys =
                r.before || r.after
                  ? r.before && r.after
                    ? changed(r.before, r.after)
                    : Object.keys(r.after ?? r.before ?? {}).filter((k) => !NOISE.has(k))
                  : [];
              return (
                <Fragment key={r.id}>
                  <tr
                    className={cn(
                      'cursor-pointer border-b border-border hover:bg-surface-2/60',
                      isOpen && 'bg-surface-2/60',
                    )}
                    onClick={() => setOpen(isOpen ? null : r.id)}
                  >
                    <td className="pl-3">
                      <ChevronRight
                        className={cn('size-3.5 text-subtle transition-transform', isOpen && 'rotate-90')}
                      />
                    </td>
                    <td className="px-2 py-2 tabular text-muted">{fmtDateTime(r.at)}</td>
                    <td className="truncate px-2 py-2">
                      {r.actor?.name ?? <span className="text-muted">{r.actorLabel ?? 'System'}</span>}
                    </td>
                    <td className="px-2 py-2">
                      <Badge tone={ACTION_TONE[r.action] ?? 'neutral'}>{r.action.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td className="truncate px-2 py-2 text-muted">{r.entityType}</td>
                    <td className="truncate px-2 py-2 text-muted">{summary(r)}</td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-border bg-surface-2/40">
                      <td />
                      <td colSpan={5} className="px-2 py-3">
                        <p className="mb-2 text-xs text-subtle">
                          Entry {r.id} · record {r.entityId ?? 'n/a'} {r.ip && `· IP ${r.ip}`}
                        </p>
                        {keys.length ? (
                          <table className="w-full text-xs">
                            <thead className="text-left text-subtle">
                              <tr>
                                <th className="w-48 py-1 font-medium">Field</th>
                                <th className="py-1 font-medium">Before</th>
                                <th className="py-1 font-medium">After</th>
                              </tr>
                            </thead>
                            <tbody className="font-mono">
                              {keys.map((k) => (
                                <tr key={k} className="align-top">
                                  <td className="py-0.5 pr-3 font-sans text-muted">{k}</td>
                                  <td className="break-all py-0.5 pr-3 text-bad">{show(r.before?.[k])}</td>
                                  <td className="break-all py-0.5 text-ok">{show(r.after?.[k])}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="text-xs text-muted">No field changes recorded for this event.</p>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      {rows.hasNextPage && (
        <div className="flex justify-center border-t border-border py-3">
          <Button variant="secondary" size="sm" loading={rows.isFetchingNextPage} onClick={() => rows.fetchNextPage()}>
            Load older entries
          </Button>
        </div>
      )}
    </Card>
  );
}
